import { addNotificationOperatorRecipientInputSchema } from '@repo/api-contracts';
import { useState } from 'react';
import { useInvalidateNotifications } from '@/entities/notification';
import { trpc } from '@/shared/api';
import { getErrorMessage } from '@/shared/lib';

export interface UseAddNotificationOperatorRecipientResult {
  /** 성공하면(설정을 다시 읽은 뒤) true. 형식 오류·서버 거부면 false. */
  readonly addRecipient: (email: string) => Promise<boolean>;
  readonly isPending: boolean;
  /** 주소 형식 오류 — 요청하지 않았다. */
  readonly fieldError: string | null;
  /** 서버 거부(중복·상한 등) 문구. */
  readonly formError: string | null;
}

/** 계약 emailSchema 와 같은 문구 — 254자 초과는 zod 영어 문구가 나오므로 스키마 문구를 꺼내 쓰지 않는다. */
const EMAIL_MESSAGE = '올바른 이메일 주소를 입력해 주세요.';
const FORM_ERROR_FALLBACK = '담당자를 추가하지 못했습니다.';

/**
 * 운영자 수신자 추가(notificationManagement.addOperatorRecipient, 알림 설계 N4). 계약 스키마로 앞뒤 공백을 지우고 소문자로 맞춘
 * 주소만 보낸다(DB 는 소문자 유일·CHECK). 상한(10명)·중복은 서버가 NOTIFICATION_RECIPIENT_LIMIT·NOTIFICATION_RECIPIENT_DUPLICATE
 * 문구로 거부한다. 성공하면 설정을 다시 읽어 목록과 기본값 안내가 바뀐다.
 */
export const useAddNotificationOperatorRecipient =
  (): UseAddNotificationOperatorRecipientResult => {
    const [fieldError, setFieldError] = useState<string | null>(null);
    const [formError, setFormError] = useState<string | null>(null);
    const invalidate = useInvalidateNotifications();
    const mutation = trpc.notificationManagement.addOperatorRecipient.useMutation({
      // 재조회 promise 를 돌려준다 — 새 행이 목록에 오기 전에 같은 주소를 또 보내지 않게 isPending 이 남는다.
      onSuccess: () => invalidate.invalidateSettings(),
    });

    const addRecipient = async (email: string): Promise<boolean> => {
      setFieldError(null);
      setFormError(null);

      const parsed = addNotificationOperatorRecipientInputSchema.safeParse({ email });

      if (!parsed.success) {
        setFieldError(EMAIL_MESSAGE);
        return false;
      }

      try {
        await mutation.mutateAsync(parsed.data);
        return true;
      } catch (cause) {
        setFormError(getErrorMessage(cause, FORM_ERROR_FALLBACK));
        return false;
      }
    };

    return { addRecipient, isPending: mutation.isPending, fieldError, formError };
  };
